import React, { useEffect, useRef, useState } from 'react';
import type { ApolloQueryResult } from '@apollo/client';
import Stories from 'react-insta-stories';

import DialogContent from '@mui/material/DialogContent';

import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import Loader from 'src/components/ui/Loader';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';
import StoryHeader from 'src/components/news/StoryHeader';
import StoryFooter from 'src/components/news/StoryFooter';

import { useViewStoryMutation, useStoriesQuery } from 'src/hooks/__generated__/queries';
import type { StoriesGroupedByOwnersQuery, Owner, Story } from 'src/types/__generated__/graphql';

import useUser from 'src/hooks/useUser';

import { colours } from 'src/constants/colours';
import { maxNumberOfStoriesInAStory, storyDefaultDuration } from 'src/constants/misc';
import { breakpoints } from 'src/constants/breakpoints';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents, { AnalyticsContextProvider } from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent, ContextFreeEventPrefixEnums } from 'src/utils/analytics';

const StyledDialog = styledComponent(Dialog)`
    width: fit-content;
    margin: auto;

    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        width: unset;

        .MuiDialog-paperScrollPaper {
            margin: 0;
            min-height: 100%;
            max-height: 100%;
            min-width: 100%;
            max-width: 100%;
        }
    }
`;

const StyledDialogContent = styledComponent(DialogContent)`
    width: 38.5rem;
    height: 40rem;
    border-radius: 0.375rem;
    margin: 0.5rem 1rem 4rem 1rem;
    padding: 0;
    position: relative;
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    background-color: ${colours.primary.main};
    > div > div > div:not(.MuiAvatar-root) {
        width: 100%;
        height: 100%;
    }
    & > div:first-of-type,
    & > div:first-of-type > div:nth-of-type(2),
    & > div:first-of-type > div:nth-of-type(2) > div {
        /* story container and div image wrapper, storyContainerStyles props doesn't work well so do it by CSS */
        width: 100% !important;
        height: 100% !important;
        background-color: ${colours.primary.main}!important;
    }
    & > div > div:first-of-type {
        /* head duration indicator, need to have a z-index = 1000 for header and this indicator should be above to have white color */
        z-index: 1010 !important;
    }

    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        position: absolute;
        width: 100%;
        height: 100%;
        margin: 0;
        border-radius: 0;
    }
`;

StyledDialog.defaultProps = {
    maxWidth: false,
};
interface Props {
    owners?: NonNullable<StoriesGroupedByOwnersQuery['storiesGroupedByOwners']>;
    openedOwner?: Owner;
    onClose?: () => void;
    handleFetchMoreStoriesByOwners?: () => Promise<ApolloQueryResult<StoriesGroupedByOwnersQuery | undefined>>;
    story?: Story; // just one story to display (home page with storyId param)
    stories?: Story[]; // stories already loaded (user profile page)
    defaultStoryIndex?: number;
}

const StoriesModal: React.FC<Props> = ({
    owners,
    openedOwner,
    onClose,
    handleFetchMoreStoriesByOwners,
    story,
    defaultStoryIndex,
    stories,
}) => {
    const { t } = useTranslation(['news']);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const currentUser = useUser();
    const [selectedStoryIndex, setSelectedStoryIndex] = useState(defaultStoryIndex ?? 0);
    const [selectedOwnerIndex, setSelectedOwnerIndex] = useState(0);
    const [isPaused, setIsPaused] = useState<boolean>();
    const [viewStory] = useViewStoryMutation();
    const { logEventDynamic } = useDynamicLogEvents();

    const variables = {
        offset: 0,
        limit: maxNumberOfStoriesInAStory,
        userId: owners ? owners[selectedOwnerIndex]?.user?.id : undefined,
        companyId: owners ? owners[selectedOwnerIndex]?.company?.id : undefined,
        orderBy: { 'story.createdAt': 'ASC' },
    };
    const { data, loading, refetch } = useStoriesQuery({
        variables,
        skip:
            Boolean(story ?? stories) ||
            !(owners?.[selectedOwnerIndex]?.user?.id ?? owners?.[selectedOwnerIndex]?.company?.id),
        fetchPolicy: 'cache-and-network',
    });

    const handleClose = async () => {
        if (!story && (owners?.[selectedOwnerIndex]?.user?.id ?? owners?.[selectedOwnerIndex]?.company?.id)) {
            await refetch(variables);
        }
        onClose?.();
    };

    useEffect(() => {
        if (openedOwner && owners) {
            const actualIndexOwner = owners.findIndex((owner) => owner.id === openedOwner.id);
            setSelectedOwnerIndex(actualIndexOwner);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openedOwner]);

    useEffect(() => {
        const storiesLength = (data?.stories ?? stories)?.length;
        if (storiesLength === 0) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            handleClose();
        }
        if (storiesLength && selectedStoryIndex > storiesLength - 1) {
            setSelectedStoryIndex(storiesLength - 1);
        }
        if ((data?.stories || stories) && defaultStoryIndex === undefined) {
            const firstNotViewed = (data?.stories ?? stories)?.findIndex(({ viewed }) => !viewed);
            setSelectedStoryIndex(firstNotViewed && Number(firstNotViewed) >= 0 ? Number(firstNotViewed) : 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.stories, stories]);

    const formatStory = (_story: Story) => ({
        ..._story,
        url: _story.medium.url,
        type: _story.medium.type,
        duration: _story.medium.duration !== 0 ? _story.medium.duration : storyDefaultDuration,
        // header Optional. Adds a header on the top. Object with heading, subheading and profileImage properties.
        // seeMore Optional. Adds a see more icon at the bottom of the story
        // seeMoreCollapsed Optional. Send custom component to be rendered instead of the default 'See More' text
        // styles: Optional. Override the default story styles mentioned below
    });

    let formatedStories = data?.stories?.map((_story) => formatStory(_story as Story)) ?? [];
    if (story) {
        formatedStories = [formatStory(story)];
    }
    if (stories) {
        formatedStories = stories.map((_story) => formatStory(_story as Story));
    }
    const actualStory = story ?? (data?.stories && data.stories[selectedStoryIndex]) ?? stories?.[selectedStoryIndex];

    const storiesLength = (data?.stories ?? stories)?.length;

    return (
        <AnalyticsContextProvider
            value={{
                eventNamePrefix: ContextFreeEventPrefixEnums.STORIE,
            }}
        >
            <StyledDialog
                onClose={handleClose}
                open={Boolean(openedOwner?.id ?? openedOwner?.user?.id ?? openedOwner?.company?.id ?? story ?? stories)}
                fullWidth
            >
                <DialogTitle onClose={handleClose}>
                    <Typography variant="h5">{t('news:stories')}</Typography>
                </DialogTitle>
                <StyledDialogContent ref={contentRef}>
                    {!loading && formatedStories.length ? (
                        <Stories
                            key={owners?.[selectedOwnerIndex]?.user?.id ?? owners?.[selectedOwnerIndex]?.company?.id}
                            stories={formatedStories}
                            defaultInterval={storyDefaultDuration}
                            // when delete last story, selectedStoryIndex is not ajusted faster than Stories component try to render so add this "hack"
                            currentIndex={
                                storiesLength && selectedStoryIndex > storiesLength - 1
                                    ? storiesLength - 1
                                    : selectedStoryIndex
                            }
                            width={contentRef.current?.clientWidth}
                            height={contentRef.current?.clientHeight}
                            loader={<Loader />}
                            isPaused={isPaused}
                            storyStyles={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                backgroundColor: colours.primary.main,
                            }}
                            onStoryEnd={(index: number) =>
                                selectedStoryIndex < formatedStories.length - 1
                                    ? setSelectedStoryIndex(index + 1)
                                    : setSelectedStoryIndex(index)
                            }
                            onStoryStart={async (index: number) => {
                                setSelectedStoryIndex(index);
                                if (currentUser && !formatedStories[index].viewed) {
                                    await viewStory({
                                        variables: {
                                            storyId: formatedStories[index].id,
                                        },
                                        refetchQueries: ['UserProfile', 'CompanyProfile'],
                                    });
                                }
                                logEventDynamic(AnalyticsEvent.See);
                            }}
                            onAllStoriesEnd={async () => {
                                if (
                                    owners &&
                                    selectedOwnerIndex === owners.length - 2 &&
                                    handleFetchMoreStoriesByOwners
                                ) {
                                    await handleFetchMoreStoriesByOwners();
                                }
                                if (owners && selectedOwnerIndex < owners.length - 1) {
                                    setSelectedOwnerIndex(selectedOwnerIndex + 1);
                                }
                            }}
                            keyboardNavigation
                        />
                    ) : null}
                    {actualStory ? (
                        <StoryHeader story={actualStory} setIsPaused={setIsPaused} onClose={handleClose} />
                    ) : null}
                    {actualStory ? <StoryFooter story={actualStory} setIsPaused={setIsPaused} /> : null}
                </StyledDialogContent>
            </StyledDialog>
        </AnalyticsContextProvider>
    );
};

export default StoriesModal;
